import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@material-ui/icons/Close";
import axios from "axios";
import { toast } from "react-toastify";
import useStyles from "../../../../styles";
import defaultSocket from "../../../socket/socket";
import { decryptData } from "../../../../crypto";

const predefinedReasons = [
  "Network Issue",
  "Student Request",
  "Device/Browser Changed",
];

function ReloginPopup({
  open,
  openReLogin,
  onClose,
  setOpenReLogin,
  rowId,
  selectedLearnerId,
  learnerName,
  rollNumber,
  decryptedToken,
  setChangeStatus,
  socket,
  onSubmit,
  isAll = false,
  title,
  subtitle,
}) {
  const classes = useStyles();

  const isOpen = open !== undefined ? open : Boolean(openReLogin);
  const effectiveLearnerId =
    selectedLearnerId || sessionStorage.getItem("selectedLearnerId");

  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (isSubmitting || !reason.trim()) return;

    try {
      setIsSubmitting(true);

      if (onSubmit) {
        await onSubmit(reason.trim(), effectiveLearnerId);
      } else if (isAll) {
        const token =
          decryptedToken || decryptData(sessionStorage.getItem("token"));
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/live/logout/all`;
        const examIdVal = !isNaN(Number(rowId)) ? Number(rowId) : rowId;
        const formattedMessage = `Admin re-logged in:${reason.trim()}`;

        const requestBody = {
          exam_id: examIdVal,
          message: formattedMessage,
          exam: {
            exam_id: examIdVal,
            message: formattedMessage,
          },
        };

        const response = await axios.put(apiUrl, requestBody, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200 || response.data?.success) {
          toast.success(
            response.data?.message ||
              "Successfully re-logged permission for all started candidates."
          );

          const activeSocket = socket || defaultSocket;
          if (activeSocket) {
            activeSocket.emit("reloginall", { examId: rowId });
          }

          if (setChangeStatus) {
            setChangeStatus((prev) =>
              prev.map((item) =>
                Number(item.examId) === Number(rowId) &&
                item.status !== "Not giving" &&
                item.status !== "Submitted"
                  ? { ...item, status: "Incomplete" }
                  : item
              )
            );
          }
        } else {
          toast.error(
            response.data?.message ||
              "Failed to re-login permission for all started candidates."
          );
        }
      } else {
        const token =
          decryptedToken || decryptData(sessionStorage.getItem("token"));
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/live/logout/one`;
        const examIdVal = !isNaN(Number(rowId)) ? Number(rowId) : rowId;
        const learnerIdVal = !isNaN(Number(effectiveLearnerId))
          ? Number(effectiveLearnerId)
          : effectiveLearnerId;

        const requestBody = {
          exam_id: examIdVal,
          learner_id: learnerIdVal,
          message: `Student Relogin:${reason.trim()}`,
          exam: {
            exam_id: examIdVal,
            learner_id: learnerIdVal,
            message: `Student Relogin:${reason.trim()}`,
          },
        };

        const response = await axios.put(apiUrl, requestBody, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        toast.success(
          response.data?.message || "Re-login processed successfully!",
          {
            position: "top-right",
            autoClose: 3000,
          }
        );

        const activeSocket = socket || defaultSocket;
        if (activeSocket) {
          activeSocket.emit("relogin", {
            examId: rowId,
            userId: effectiveLearnerId,
          });
        }

        if (setChangeStatus) {
          setChangeStatus((prev) => {
            const existingIndex = prev.findIndex(
              (item) =>
                Number(item.userId) === Number(effectiveLearnerId) &&
                Number(item.examId) === Number(rowId)
            );

            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = {
                ...updated[existingIndex],
                status: "Incomplete",
              };
              return updated;
            }

            return prev;
          });
        }
      }

      setReason("");
      if (onClose) onClose();
      if (setOpenReLogin) setOpenReLogin(false);
    } catch (error) {
      console.error("Re-login failed:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred while logging out learner.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePredefinedReason = (message) => {
    if (isSubmitting) return;
    setReason(message);
  };

  const handleClose = () => {
    if (isSubmitting) return;

    setReason("");
    if (onClose) onClose();
    if (setOpenReLogin) setOpenReLogin(false);
  };

  const displayTitle =
    title || (isAll ? "Re-Login All Students" : "Student Re-Login");
  const displaySubtitle =
    subtitle ||
    (isAll
      ? "Please provide a valid reason for requiring all students to re-login."
      : "Please provide a valid reason for requiring the student to re-login.");

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={handleClose}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {displayTitle}
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          {displaySubtitle}
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={4}
          // label="Re-Login Reason"
          placeholder="Enter reason..."
          value={reason}
          disabled={isSubmitting}
          onChange={(e) => setReason(e.target.value)}
        />

        <Box
          sx={{
            display: "flex",
            gap: 1,
            mt: 1,
            flexWrap: "wrap",
          }}
        >
          {predefinedReasons.map((message, index) => (
            <Button
              key={index}
              variant="outlined"
              disabled={isSubmitting}
              onClick={() => handlePredefinedReason(message)}
              className={classes.custombtnoutline}
            >
              {message}
            </Button>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          className={`${classes.custombtnblue} ${classes.mt1} ${classes.mr1} ${classes.h35px}`}
          style={{
            opacity: reason ? 1 : 0.5,
          }}
          disabled={isSubmitting || !reason.trim()}
          onClick={handleSubmit}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Processing...
            </>
          ) : (
            "Continue & Re-Login"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { ReloginPopup as ReLoginReason };
export default ReloginPopup;

